import React, { memo } from 'react';

import {
  Badge,
  Tooltip,
  Typography,
} from 'antd';

import CustomProgress
  from '../../../../components/V2/CustomProgress/CustomProgress';
import { useTheme } from '../../../../context/ThemeProvider';
import { useTQConnectConfig } from '../../../../hooks/queries/tqConnectQueries';
import {
  getSingularOrPlural,
  tintColor,
} from '../../../../shared/utils';
import {
  ITQRatingCategories,
  ITQRatingCategory,
  TQRatingCategoryValue,
} from '../../../../types/tqConnectTypes';
import {
  ITQProfileReviewsStatistics,
} from '../../../../types/tqConnectTypes/user';

interface Props {
  selectedRating: TQRatingCategoryValue | null;
  onSelectRating: (rating: TQRatingCategoryValue | null) => void;
  stats: ITQProfileReviewsStatistics;
}

const ReviewsFilters: React.FC<Props> = ({
  selectedRating,
  onSelectRating,
  stats,
}) => {
  const { colors: COLORS } = useTheme();

  const { data: tqConnectConfigResult } = useTQConnectConfig();

  const tqConnectConfig = tqConnectConfigResult?.tqConnectConfig;
  const ratingCategories = (tqConnectConfig?.ratingCategories ||
    {}) as ITQRatingCategories;
  const ratings: [TQRatingCategoryValue, ITQRatingCategory][] = Object.entries(
    ratingCategories,
  ).reverse() as [TQRatingCategoryValue, ITQRatingCategory][];
  const selectedRatingCategory = selectedRating
    ? ratingCategories[selectedRating]
    : null;

  return (
    <div className="reviews__filters">
      <div className="filters__list">
        {ratings.map(([key, rating]) => {
          const label = rating.logo;
          const count = stats[key];
          const isSelected = selectedRating === key;

          return (
            <div
              key={key}
              className={`filters__list__option ${
                isSelected ? 'filters__list__option--selected' : ''
              }`}
              onClick={() => {
                onSelectRating(isSelected ? null : key);
              }}>
              <Tooltip
                title={
                  (count > 0 &&
                    (isSelected
                      ? 'Clear filter'
                      : `Show ${count} ${rating.name.toLowerCase()} ${getSingularOrPlural(count, 'review')}`)) ||
                  undefined
                }
                destroyTooltipOnHide
                mouseEnterDelay={1}>
                <Badge
                  count={count}
                  overflowCount={99}
                  size="small"
                  offset={[-2, 30]}
                  color={COLORS.TEXT1}
                  style={{
                    paddingInline: 4,
                    fontSize: 8,
                    color: COLORS.WHITE,
                  }}
                  title={
                    count > 0
                      ? `${count} ${rating.name.toLowerCase()} ${getSingularOrPlural(count, 'review')}`
                      : undefined
                  }>
                  <CustomProgress
                    type="circle"
                    width={40}
                    percent={count}
                    strokeWidth={6}
                    status="normal"
                    strokeColor={rating.color || COLORS.SUCCESS}
                    showInfo
                    className="option__progress"
                    format={() => {
                      return <span style={{ fontSize: 24 }}>{label}</span>;
                    }}
                    style={{
                      borderRadius: '50%',
                      backgroundColor: rating.color
                        ? tintColor(rating.color, 90)
                        : COLORS.SUCCESS_FILL,
                    }}
                  />
                </Badge>
              </Tooltip>
            </div>
          );
        })}
      </div>

      {selectedRatingCategory ? (
        <div className="filters__current-rating">
          <Typography.Title level={5} className="current-rating__title">
            {selectedRatingCategory.name}
          </Typography.Title>

          <Typography.Text className="current-rating__description">
            {selectedRatingCategory.feedback ||
              selectedRatingCategory.placeholder}
          </Typography.Text>
        </div>
      ) : null}
    </div>
  );
};

export default memo(ReviewsFilters);
